import { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { portalMode, isPortalRoleAllowed } from '@/config/portal';
import { getApiErrorMessage } from '@/utils/apiError';
import { toast } from 'react-toastify';
import { ShieldCheck, RefreshCw, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { authApi } from '@/api/auth';
import { useAuthStore, isDashboardTokenUsable } from '@/store/authStore';
import { storage } from '@/utils/storage';
import { cn } from '@/lib/utils';

const OTP_LENGTH = 4;
const RESEND_COUNTDOWN = 60;

export function VerifyOtpPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { t } = useTranslation();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN);
  const [canResend, setCanResend] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const verificationPending = useRef(false);
  const resendPending = useRef(false);
  const [isResending, setIsResending] = useState(false);

  const mobile = sessionStorage.getItem('login_mobile');

  useEffect(() => {
    // Focus only after React has re-enabled the fields following a rejection.
    if (!isLoading && otp.every((digit) => !digit)) inputRefs.current[0]?.focus();
  }, [isLoading, otp]);

  useEffect(() => {
    if (!mobile) {
      navigate('/login');
      return;
    }

    // Focus first input
    inputRefs.current[0]?.focus();
  }, [mobile, navigate]);

  useEffect(() => {
    if (!mobile || canResend) return;

    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [mobile, canResend]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    // SMS autofill can place the entire code into a single input.
    if (value.length > 1) {
      const digits = value.slice(0, OTP_LENGTH).split('');
      setOtp(Array.from({ length: OTP_LENGTH }, (_, digitIndex) => digits[digitIndex] ?? ''));
      inputRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
      if (digits.length === OTP_LENGTH) void handleVerify(digits.join(''));
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Move to next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto submit when complete
    if (newOtp.every((digit) => digit) && newOtp.join('').length === OTP_LENGTH) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim().slice(0, OTP_LENGTH);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = Array<string>(OTP_LENGTH).fill('');
    pastedData.split('').forEach((digit, index) => {
      if (index < OTP_LENGTH) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);
    inputRefs.current[Math.min(pastedData.length, OTP_LENGTH - 1)]?.focus();

    if (pastedData.length === OTP_LENGTH) {
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (otpCode: string) => {
    if (!mobile || verificationPending.current || !/^\d{4}$/.test(otpCode)) return;

    verificationPending.current = true;
    setVerificationError('');
    setIsLoading(true);
    try {
      const tokenResponse = await authApi.verifyOtp({
        Mobile: mobile,
        OTP: otpCode,
        ClientType: portalMode === 'provider' ? 'provider' : 'support',
      });

      if (!isDashboardTokenUsable(tokenResponse.tokenString)) throw new Error(t('auth.login.portalAccessDenied'));

      // Store token first so getProfile can use it
      storage.setAuthMethod('otp');
      storage.setAccessToken(tokenResponse.tokenString);
      storage.setRefreshToken(tokenResponse.refreshToken);

      // Get user profile
      const profile = portalMode === 'provider' ? await authApi.getProviderProfile() : await authApi.getProfile();

      // Login to store (this also sets tokens but we need them before getProfile)
      login(tokenResponse.tokenString, tokenResponse.refreshToken, profile);

      if (!useAuthStore.getState().isAuthenticated || !isPortalRoleAllowed(useAuthStore.getState().role)) {
        useAuthStore.getState().logout();
        throw new Error(t('auth.login.portalAccessDenied'));
      }

      // Clear session storage
      sessionStorage.removeItem('login_mobile');

      toast.success(t('auth.otp.loginSuccess'));
      const requested = sessionStorage.getItem('login_return');
      const destination = requested === '/dispatcher' || requested === '/field' ? requested : '/';
      sessionStorage.removeItem('login_return');
      navigate(destination);
    } catch (error) {
      // Profile loading may fail after Identity has issued tokens. A failed
      // sign-in must not leave a partially authenticated browser behind.
      storage.clearTokens();
      setVerificationError(getApiErrorMessage(error, t('auth.otp.invalidCode')));
      setOtp(Array(OTP_LENGTH).fill(''));
    } finally {
      verificationPending.current = false;
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!mobile || !canResend || resendPending.current || verificationPending.current) return;

    resendPending.current = true;
    setIsResending(true);
    try {
      await authApi.forceOtp(mobile, portalMode === 'provider' ? 'provider' : 'support');
      toast.success(t('auth.otp.resendSuccess'));
      setCountdown(RESEND_COUNTDOWN);
      setCanResend(false);
    } catch (error) {
      setVerificationError(getApiErrorMessage(error, t('common.error')));
    } finally {
      resendPending.current = false;
      setIsResending(false);
    }
  };

  const isComplete = otp.every((digit) => digit);

  if (portalMode === 'superadmin') return <Navigate to="/admin-login" replace />;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 rounded-md bg-primary flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <div className="pt-1">
          <h1 className="text-lg font-semibold text-foreground">{t('auth.otp.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('auth.otp.subtitle')}
          </p>
          <p className="font-medium text-foreground mt-1" dir="ltr">
            {mobile}
          </p>
        </div>
      </div>

      {verificationError && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{verificationError}</p>}

      {/* OTP Input */}
      <div className="flex justify-center gap-2.5" dir="ltr">
        {otp.map((digit, index) => (
          <div key={index} className="relative">
            <input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={OTP_LENGTH}
              aria-label={`${t('auth.otp.codeLabel')} ${index + 1}`}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              disabled={isLoading}
              className={cn(
                "w-11 h-12 text-center text-lg font-semibold rounded-md border transition-colors bg-background outline-none",
                focusedIndex === index ? "border-primary ring-1 ring-primary" : "border-input",
                digit ? "border-primary bg-primary/5" : "",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            />
            {/* Cursor animation when focused and empty */}
            {focusedIndex === index && !digit && (
              <span className="absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary animate-pulse" />
            )}
          </div>
        ))}
      </div>

      {/* Verify Button */}
      <Button
        onClick={() => handleVerify(otp.join(''))}
        loading={isLoading}
        disabled={!isComplete}
        className="w-full"
      >
        {!isLoading && t('auth.otp.verifyButton')}
      </Button>

      {/* Resend Timer */}
      <div className="text-center">
        {canResend ? (
          <button
            onClick={handleResend}
            disabled={isResending || isLoading}
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline underline-offset-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            {t('auth.otp.resend')}
          </button>
        ) : (
          <div className="space-y-0.5">
            <p className="text-muted-foreground text-sm">{t('auth.otp.didntReceive')}</p>
            <p className="text-foreground text-sm">
              {t('auth.otp.resendIn')}{' '}
              <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium text-sm">
                {countdown}
              </span>
              {' '}{t('auth.otp.seconds')}
            </p>
          </div>
        )}
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate('/login')}
        className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors py-1.5"
      >
        <ArrowRight className="w-4 h-4" />
        {t('auth.otp.changeNumber')}
      </button>
    </div>
  );
}
